// One scene-free pass for the pair: conditioning → decoders → polar lift → packed splat words.

import { buildCond, gazeToHeadSpace, pluckerFromCamera } from './eyeCond.ts'
import type { CondOwners } from './eyeCond.ts'
import type { EyeMeta } from './eyeChart.ts'
import type { EyeDecoderMaps, EyeDecoders } from './eyeDecoders.ts'
import type { EyeSplatMesh } from './eyeSplatMesh.ts'
import type { GpuEyeLifter } from './GpuEyeLifter.ts'
import type { EyePose } from './controller/index.ts'
import { EYE_SIDES } from './eyeSide.ts'
import type { EyeSide } from './eyeSide.ts'
import { vector3 } from '../math/index.ts'
import type { Vector3 } from '../math/index.ts'

/** What the appearance decoder is conditioned on — the plücker rays come from here. */
export type CameraView = {
  origin: Vector3
  forward: Vector3
  fovDegrees: number
}

/** Structural view of a three.js camera, so this module needs no THREE import. */
export type CameraLike = {
  position: { x: number, y: number, z: number }
  matrixWorld: { elements: ArrayLike<number> }
  fov?: number
  updateMatrixWorld?: () => void
}

// THREE's own convention: the view direction is the negated third column of matrixWorld.
export function cameraView(camera: CameraLike): CameraView {
  camera.updateMatrixWorld?.()
  const elements = camera.matrixWorld.elements
  return {
    origin: [camera.position.x, camera.position.y, camera.position.z],
    forward: vector3.normalize([-elements[8], -elements[9], -elements[10]]),
    fovDegrees: camera.fov ?? 45,
  }
}

export type EyePassOptions = {
  decoders: EyeDecoders
  lifter: GpuEyeLifter
  splatMesh: EyeSplatMesh
  meta: EyeMeta
  condOwners: CondOwners
}

export type EyePass = ReturnType<typeof createEyePass>

export function createEyePass(options: EyePassOptions) {
  const { decoders, lifter, splatMesh, meta, condOwners } = options
  const words = new Uint32Array(splatMesh.capacity * splatMesh.wordsPerSlot)
  let passes = 0
  let colorChannels = 0

  return {
    get passes(): number { return passes },

    /** appr's channel count from the last run. 3 means the bundle carries NO specular — the
     *  glint needs a 4th channel, so no light set can produce one. */
    get colorChannels(): number { return colorChannels },

    /** Diagnostics: a copy of the last pass's ExtSplats words, for a test to decode. */
    packedWords(): Uint32Array { return Uint32Array.from(words) },

    /** One pass, each chart conditioned on its OWNER eye's gaze. The caller serializes. */
    async run(poses: Record<EyeSide, EyePose>, view: CameraView): Promise<void> {
      const lookAt = vector3.add(view.origin, view.forward)
      const plucker = pluckerFromCamera(view.origin, lookAt, view.fovDegrees, meta.uvResolution)
      const maps = {} as Record<EyeSide, EyeDecoderMaps>
      for (const side of EYE_SIDES) {
        const gaze = poses[condOwners.owners[side]].targetDirection
        const cond = buildCond(gazeToHeadSpace(gaze, meta.headPose), meta.headPose)
        maps[side] = await decoders.run(cond, plucker)
      }
      colorChannels = maps.left.colorChannels
      await lifter.liftBoth({
        left: { pose: poses.left, maps: maps.left },
        right: { pose: poses.right, maps: maps.right },
      }, view.origin, words)
      splatMesh.updateSplats(words)
      passes++
    },
  }
}
